import HTML from "shared/Dictionary/Dictionary.html";
import {
	CollectionView,
	EventListener,
	View,
	ViewData
} from "silver";
import { DictionarySourceInterface, DictionaryEntry } from "shared/Dictionary/DictionarySource";
import { Expander } from "shared/Components/Expander";
import { DictionaryLang } from "Lang/Widgets";

interface DictionaryEntryView extends View<DictionaryEntry> {
	exampleButton: HTMLButtonElement;
	examples: HTMLElement;
	exampleExpander?: Expander;
	relatedButton: HTMLButtonElement;
	relatedWords: HTMLElement;
	relatedExpander?: Expander;
}

interface DictionaryViewData extends ViewData {
	lang : DictionaryLang | undefined;
	searching: boolean;
	noResults: boolean;
	showUserInput: boolean;
	entries: DictionaryEntry[];
	suggestions: string[];
}

interface DictionaryView extends View<DictionaryViewData> {
	searchInput: HTMLInputElement;
	entries: CollectionView<DictionaryEntry>;
	suggestions: CollectionView<{ $value: string }>;
	audio: HTMLAudioElement;
}

export class Dictionary {

	public onResultDisplay = EventListener.create<{ word: string }>("resultDisplay");

	protected view: DictionaryView;
	protected searchTimeout: number = -1;
	private expanders: Expander[] = [];

	constructor(
		protected source: DictionarySourceInterface,
		protected lang: DictionaryLang | undefined
	) {
		this.view = new View<DictionaryViewData>(HTML, {
			handleSuggestionClick: (e: Event, data: { $value: string }) => {
				this.search(data.$value).catch(console.error);
			},
			handleEntryAdded: (view: DictionaryEntryView) => {
				this.makeExpander(view.exampleButton, view.examples);
				this.makeExpander(view.relatedButton, view.relatedWords);
			},
			handleFormSubmit: (e: Event) => {
				this.doSearch(this.view.searchInput.value).catch(console.error);
				e.preventDefault();
				e.stopPropagation();
			},
			handleAudioClipClick: (e: Event, data : DictionaryEntry) => {
				if (! data.wavFile) {
					return;
				}
				this.view.audio.src = data.wavFile;
				this.view.audio.load();
				this.view.audio.play();
			}
		}, {
			lang,
			searching:     false,
			noResults:     false,
			showUserInput: true,
			entries:       [],
			suggestions:   []
		}) as DictionaryView;
	}

	get element() {
		return this.view.element;
	}

	public toggleUserInput(showUserInput : boolean) {
		this.view.set({ showUserInput });
	}

	public setLanguage(lang: DictionaryLang): void {
		this.view.set({ lang });
	}

	public search(word: string): Promise<boolean> {
		this.view.searchInput.value = word;
		return this.doSearch(word);
	}

	public focus(): void {
		this.view.searchInput.focus();
	}

	private doSearch(word: string): Promise<boolean> {
		word = word.trim();
		if (!word) {
			this.view.set({ searching: false, noResults: false });
			return Promise.resolve(false);
		}

		this.view.set({
			searching:   true,
			noResults:   false,
			suggestions: [],
			entries:     []
		});

		this.expanders.forEach((e) => e.onChange.clear());
		this.expanders = [];

		return this.source.search(word).then(results => {
			const entries = results.entries.filter(v => v.definitions.length !== 0);

			try {
				this.view.set({
					entries,
					noResults:   entries.length === 0 && results.suggestions.length === 0,
					suggestions: results.suggestions,
					searching:   false
				});
			} catch (e) {
				console.error(e);
			}
			this.onResultDisplay.trigger({ word });
			return entries.length > 0;
		});
	}

	private makeExpander(source: HTMLElement, target: HTMLElement): void {
		const expander = new Expander(source, target, {
			sourceOpenClass: "-open",
			slideDuration:   300
		});
		this.expanders.push(expander);
		expander.open = false;
	}
}
